export type AdminTab<T extends string> = {
  id: T;
  label: string;
  count?: number;
};

export type AdminTabsProps<T extends string> = {
  tabs: AdminTab<T>[];
  activeTab: T;
  onChange: (tab: T) => void;
  ariaLabel: string;
  idPrefix: string;
  density?: 'default' | 'compact';
};

export const AdminTabs = <T extends string>({
  tabs,
  activeTab,
  onChange,
  ariaLabel,
  idPrefix,
  density = 'default',
}: AdminTabsProps<T>) => (
  <div
    role="tablist"
    aria-label={ariaLabel}
    className="flex shrink-0 overflow-x-auto border-b border-[var(--ots-outline-variant)] px-4 sm:px-6"
  >
    {tabs.map((tab) => {
      const selected = tab.id === activeTab;

      return (
        <button
          key={tab.id}
          id={`${idPrefix}-${tab.id}-tab`}
          type="button"
          role="tab"
          aria-selected={selected}
          aria-controls={`${idPrefix}-${tab.id}-panel`}
          onClick={() => onChange(tab.id)}
          className={[
            'relative px-3 font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--ots-primary)]',
            density === 'compact' ? 'min-h-10 text-xs' : 'min-h-14 text-sm',
            selected
              ? 'text-[var(--ots-secondary)]'
              : 'text-[var(--ots-text-muted)] hover:text-[var(--ots-secondary)]',
          ].join(' ')}
        >
          {tab.label}
          {typeof tab.count === 'number' && (
            <span className="ml-1.5 text-xs font-medium text-[var(--ots-text-muted)]">
              ({tab.count})
            </span>
          )}
          {selected && (
            <span className="absolute inset-x-3 bottom-0 h-0.5 bg-[var(--ots-accent)]" />
          )}
        </button>
      );
    })}
  </div>
);
